import {
    IDocumentValidationTouchpoint,
    IValidationResultsCollector,
    RegisterValidationFunctionOptions,
    OldValidationFn,
    NewValidationFn,
    ValidationFn,
} from '../../ScriptingEngineEnvironment/Touchpoint/DocumentValidation';
import { IDocumentViewModel } from '../../../../services/Document/Api';
import { ExecutionContext } from '../../../../services/backendServices/ViewModels';
import { AxiosInstance } from 'axios';
import { ContextInformationCollector } from './ContextInformationCollector';
import { RuntimeRuleNonLibrary } from '../../CoreEngine/Utility/RuntimeRule';
import { ValidationResultsCollector } from './ValidationResultsCollector';
import { PODocumentsForInvoice } from './extractPONumbers';

type RuleRegistration = {
    scriptName: string;
    dpsRuleName: RuntimeRuleNonLibrary['dpsRuleName'];
    configParams: RuntimeRuleNonLibrary['configParams'];
    ordinal: RuntimeRuleNonLibrary['ordinal'];
    validations: ValidationFn[];
};

export class DocumentValidationTouchpoint implements IDocumentValidationTouchpoint {
    private registeredRules = new Map<string, RuleRegistration>();

    private currentScript: string | null = null;

    private hasCompletedRegistration = false;

    private results = new ValidationResultsCollector();

    constructor(
        private readonly context: ExecutionContext,
        private api: () => AxiosInstance,
        private buyerCompanyId?: number,
        private senderCompanyId?: number,
        private parentCompanyId?: number, // for multi-company scenarios
        private writeToLog?: (message: string) => void
    ) {}

    setCurrentScript(rule: RuntimeRuleNonLibrary) {
        if (this.hasCompletedRegistration) {
            throw new Error('Registration has already been completed.');
        }

        this.currentScript = rule.name;

        this.registeredRules.set(rule.name, {
            scriptName: rule.name,
            dpsRuleName: rule.dpsRuleName,
            configParams: rule.configParams,
            ordinal: rule.ordinal,
            validations: [],
        });
    }

    completeRegistration() {
        this.currentScript = null;
        this.hasCompletedRegistration = true;
    }

    register(options: RegisterValidationFunctionOptions): void {
        if (this.hasCompletedRegistration) {
            throw new Error('Tried to register a validation function after registration was completed.');
        }

        if (!this.currentScript) {
            throw new Error('Tried to register a validation function before setting a Script.');
        }

        const scriptConfig = this.registeredRules.get(this.currentScript)!;

        this.registeredRules.set(this.currentScript, {
            ...scriptConfig,
            validations: [...scriptConfig.validations, options.validate],
        });
    }

    async executeValidationFunctions({
        document,
        contextInformationCollector,
        poDocumentsForInvoice,
    }: {
        document: IDocumentViewModel;
        contextInformationCollector: ContextInformationCollector;
        poDocumentsForInvoice: PODocumentsForInvoice;
    }) {
        if (!this.hasCompletedRegistration) {
            throw new Error('Tried to execute validation functions before registration was completed.');
        }

        this.results = new ValidationResultsCollector();

        if (this.registeredRules.size === 0) {
            this.writeToLog?.('No js scripts attached to mapping');
            return this.results.getResults();
        }

        const rulesGroupedByOrdinal = Array.from(this.registeredRules.values()).reduce((rulesRecord, rule) => {
            rulesRecord[rule.ordinal] ??= [];

            rulesRecord[rule.ordinal].push(rule);

            return rulesRecord;
        }, {} as Record<number, RuleRegistration[]>);

        const ruleExecutionGroups = Object.entries(rulesGroupedByOrdinal).map(([ordinal, rules]) => {
            const groupExecution = async () => {
                const ruleExecutionPromises = rules.map(
                    async ({ scriptName, configParams, validations, dpsRuleName }) => {
                        this.writeToLog?.(`Executing validation functions for script: ${scriptName}`);

                        const validationPromises = validations.map(async (validationFn) => {
                            if (validationFn.length === 1) {
                                await (validationFn as NewValidationFn)({
                                    context: this.context,
                                    api: this.api,
                                    results: this.results,
                                    document,
                                    configParams,
                                    buyerCompanyId: this.buyerCompanyId,
                                    senderCompanyId: this.senderCompanyId,
                                    contextInformation: contextInformationCollector.getContextInformation(),
                                    parentCompanyId: this.parentCompanyId,
                                    dpsRuleName,
                                    poDocuments: poDocumentsForInvoice,
                                });
                            } else {
                                await (validationFn as OldValidationFn)(
                                    this.context,
                                    this.api,
                                    this.results,
                                    document,
                                    configParams,
                                    this.buyerCompanyId,
                                    this.senderCompanyId,
                                    contextInformationCollector.getContextInformation(),
                                    this.parentCompanyId,
                                    dpsRuleName
                                );
                            }
                        });
                        await Promise.all(validationPromises);

                        this.writeToLog?.(`Finished executing validation functions for script: ${scriptName}`);
                    }
                );

                await Promise.all(ruleExecutionPromises);
            };

            return [ordinal, groupExecution] as const;
        });

        ruleExecutionGroups.sort((a, b) => Number(a[0]) - Number(b[0]));

        for await (const [ordinal, groupExecution] of ruleExecutionGroups) {
            this.writeToLog?.(`Executing validation functions for ordinal: ${ordinal}`);

            await groupExecution();

            this.writeToLog?.(`Finished executing validation functions for ordinal: ${ordinal}`);
        }

        return this.results.getResults();
    }

    public getResults(): IValidationResultsCollector {
        return this.results;
    }
}
